import React, { useEffect, useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Stack, Button,
  Typography, Alert, Chip, FormControlLabel, Switch, TextField, CircularProgress,
  Divider, IconButton, Tooltip,
} from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import CloseIcon from '@mui/icons-material/Close'
import { apiService } from '../../services/api'

/**
 * Manual "send report" dialog for one scope.
 *
 * Props:
 *   open, onClose
 *   operator       'plus' | 'orange' | 'tmobile'
 *   scope          'agent' | 'region' | 'district' | 'kam'  (default: 'agent')
 *   scopeValue     identifier within scope
 *   agentKey       (legacy alias for scope='agent', scopeValue=agentKey)
 *   reportType     backend report variant (default: standard)
 *   periodStart, periodEnd  YYYY-MM-DD optional
 */
const SCOPE_LABEL_PL = {
  agent: 'Agent',
  region: 'Region',
  district: 'Dystrykt',
  kam: 'KAM',
}

const SendReportModal = ({
  open, onClose, operator,
  scope = 'agent', scopeValue, agentKey,
  reportType = 'standard',
  periodStart, periodEnd,
}) => {
  // Backwards-compat: agentKey prop maps to scope='agent' / scopeValue=agentKey
  const effectiveScope = scope || 'agent'
  const effectiveValue = scopeValue ?? agentKey

  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [preview, setPreview] = useState(null)
  const [forceTest, setForceTest] = useState(true)
  const [additionalTo, setAdditionalTo] = useState('')

  const loadPreview = async () => {
    if (!effectiveValue) return
    setLoading(true); setError(null); setSuccess(null)
    try {
      const resp = await apiService.previewScopeReport({
        operator, scope: effectiveScope, scope_value: effectiveValue,
        report_type: reportType,
        period_start: periodStart, period_end: periodEnd,
        force_test: forceTest,
        additional_to: additionalTo || null,
      })
      setPreview(resp.data)
    } catch (err) {
      setError(err?.response?.data?.detail || err.message)
      setPreview(null)
    } finally { setLoading(false) }
  }

  useEffect(() => {
    if (open) loadPreview()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, operator, effectiveScope, effectiveValue, reportType, forceTest])

  const send = async () => {
    setSending(true); setError(null); setSuccess(null)
    try {
      const resp = await apiService.sendScopeReport({
        operator, scope: effectiveScope, scope_value: effectiveValue,
        report_type: reportType,
        period_start: periodStart, period_end: periodEnd,
        force_test: forceTest,
        additional_to: additionalTo || null,
      })
      setSuccess(resp.data)
    } catch (err) {
      setError(err?.response?.data?.detail || err.message)
    } finally { setSending(false) }
  }

  const close = () => {
    setPreview(null); setSuccess(null); setError(null); setAdditionalTo('')
    onClose?.()
  }

  return (
    <Dialog open={open} onClose={close} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            Wyślij raport — <strong>{operator?.toUpperCase()}</strong> ·{' '}
            <Chip size="small" label={SCOPE_LABEL_PL[effectiveScope] || effectiveScope}
                  sx={{ mx: 0.5 }} variant="outlined" />
            {effectiveValue}
            {preview?.effective_mode && (
              <Chip
                size="small"
                sx={{ ml: 1 }}
                label={preview.effective_mode === 'test' ? '🧪 TEST' : '🚀 PRODUKCJA'}
                color={preview.effective_mode === 'test' ? 'warning' : 'success'}
              />
            )}
          </Box>
          <IconButton onClick={close}><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <FormControlLabel
            control={<Switch checked={forceTest} onChange={(e) => setForceTest(e.target.checked)} />}
            label="Wymuś tryb testowy (override globalnych ustawień)"
          />
          <TextField
            size="small" label="Dodatkowe adresy (CC)" value={additionalTo}
            onChange={(e) => setAdditionalTo(e.target.value)}
            placeholder="opcjonalnie: dodaj@adres.pl"
            sx={{ minWidth: 280 }}
          />
          <Button size="small" onClick={loadPreview} disabled={loading}>Odśwież podgląd</Button>
        </Stack>

        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
        {success && (
          <Alert severity="success">
            ✅ Wysłano! Status: <strong>{success.status}</strong>, log_id: {success.log_id}.<br/>
            Wysłano do: {(success.actual_recipients || []).map(r => r.email).join(', ')}<br/>
            Tryb: <strong>{success.effective_mode}</strong>
          </Alert>
        )}

        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}

        {preview && !loading && (
          <>
            {preview.effective_mode === 'test' ? (
              <Alert severity="info">
                <strong>🧪 Tryb testowy</strong> — mail zostanie wysłany TYLKO na adres testowy:
                <ul style={{ margin: '8px 0 0' }}>
                  {(preview.actual_recipients || []).map((r, i) => (
                    <li key={i}><code>{r.email}</code></li>
                  ))}
                </ul>
                Realni odbiorcy (zostaną tylko zalogowani):
                <ul style={{ margin: '8px 0 0', color: '#6b7280' }}>
                  {(preview.intended_recipients || []).map((r, i) => (
                    <li key={i}>
                      <strong>{r.role}</strong>: {r.name} <code>&lt;{r.email}&gt;</code>
                    </li>
                  ))}
                  {(preview.intended_recipients || []).length === 0 && <li>(brak adresatów)</li>}
                </ul>
              </Alert>
            ) : (
              <Alert severity="warning">
                <strong>🚀 Produkcja</strong> — mail trafi do realnych odbiorców:
                <ul style={{ margin: '8px 0 0' }}>
                  {(preview.actual_recipients || []).map((r, i) => (
                    <li key={i}>
                      <strong>{r.role}</strong>: {r.name} <code>&lt;{r.email}&gt;</code>
                    </li>
                  ))}
                </ul>
              </Alert>
            )}

            <Divider />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Podgląd treści maila ({preview.totals?.salon_count} salonów)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                <strong>Subject:</strong> {preview.subject}
              </Typography>
              <Box
                component="iframe"
                title="Email preview"
                srcDoc={preview.html}
                sx={{
                  width: '100%', height: 600,
                  border: '1px solid #e5e7eb', borderRadius: 1, background: '#f4f4f5',
                }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              Załącznik: <code>{preview.attachment_filename}</code>
            </Typography>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={close}>Anuluj</Button>
        <Button
          variant="contained" startIcon={<SendIcon />}
          color={preview?.effective_mode === 'test' ? 'warning' : 'success'}
          onClick={send}
          disabled={sending || !preview}
        >
          {sending ? 'Wysyłam…' :
            preview?.effective_mode === 'test' ? 'Wyślij (TEST)' : '🚀 Wyślij na realne adresy'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SendReportModal
